import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText } from "lucide-react";

export default function CatalogImportHistory() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/catalog/history"); setItems(r.data); }
    catch { toast.error("Errore caricamento storico"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try { const r = await api.get(`/catalog/history/${id}`); setDetail(r.data); }
    catch { toast.error("Errore apertura batch"); }
  };
  const downloadCsv = async (id) => {
    const r = await api.get(`/catalog/history/${id}/report.csv`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" })); a.download = `import_${id.slice(0,8)}.csv`; a.click();
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-3" data-testid="import-history-page">
      <Link to="/catalogo" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Catalogo prodotti</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Catalogo</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Storico importazioni</h1>
      </div>

      {loading && <div className="text-center text-slate-500 py-6">Caricamento…</div>}
      {items.length === 0 && !loading && <div className="ama-card p-8 text-center text-slate-500">Nessuna importazione ancora eseguita.</div>}

      <div className="grid gap-2">
        {items.map((b) => (
          <div key={b.id} className="ama-card p-4 flex items-center gap-3 flex-wrap" data-testid={`history-row-${b.id}`}>
            <FileText className="w-4 h-4 text-[#d63384]" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-mono text-xs text-slate-500">{b.id.slice(0, 8)}</div>
                <Badge className={b.environment === "production" ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}>{b.environment === "production" ? "Produzione" : "Anteprima"}</Badge>
                <div className="text-xs text-slate-500">{new Date(b.started_at).toLocaleString("it-IT")} · {b.administrator_email}</div>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Creati: <b>{b.counts?.created || 0}</b> · Aggiornati: <b>{b.counts?.updated || 0}</b> · Invariati: <b>{b.counts?.unchanged || 0}</b> · Ignorati: <b>{b.counts?.skipped || 0}</b> · Falliti: <b>{b.counts?.failed || 0}</b>
              </div>
              <div className="text-[11px] text-slate-500">{b.source} · {b.pages_analysed} pagine analizzate</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => openDetail(b.id)}>Dettaglio</Button>
              <Button size="sm" variant="outline" onClick={() => downloadCsv(b.id)}><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
          </div>
        ))}
      </div>

      {detail && (
        <Card className="ama-card border-0 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Dettaglio batch {detail.id.slice(0, 8)}</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setDetail(null)}>Chiudi</Button>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-slate-500 mb-2">Sito: <b>{detail.source}</b> · Ambiente: <b>{detail.environment}</b> · Amministratore: <b>{detail.administrator_email}</b></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3">Codice</th>
                    <th className="text-left py-2 px-3">Titolo</th>
                    <th className="text-left py-2 px-3">Esito</th>
                    <th className="text-left py-2 px-3">Prodotto DB</th>
                    <th className="text-left py-2 px-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.results || []).map((r) => (
                    <tr key={r.row_id} className="border-t border-slate-100">
                      <td className="py-2 px-3 font-mono text-xs">{r.product_code || "—"}</td>
                      <td className="py-2 px-3">{r.product_title || "—"}</td>
                      <td className="py-2 px-3"><Badge variant="outline">{r.outcome}</Badge></td>
                      <td className="py-2 px-3 font-mono text-[10px]">{r.product_id ? r.product_id.slice(0, 8) : "—"}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{r.reason || r.message || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
